import React, { useEffect, useState } from 'react';
import { Popper, useIsMobile } from '@amzn/storm-ui';
import { angleDown } from '@amzn/storm-ui-icons';
import AdaptiveHelpTip from '@amzn/storm-ui-adaptive-helptip';
import { logger } from '../../../../../commons/utils/logger';
import { requestWithRetry } from '../../../../../commons/utils/asyncRequest';
import {
  PopoverMultipleLines,
  PopoverWithContent,
  TextLinkPopover,
  TextLinkPopoverRemote,
} from '../types';
import { TextLinkButton, TextLinkValue, TextLinkIcon } from './styles';
import {
  isValidPopoverResponse,
  DynamicPopover,
  MultipleLines,
  KeyLabel,
} from '../utils';

export const ajaxGetPopoverResponse = async (
  url: string,
): Promise<PopoverMultipleLines | PopoverWithContent | string | null> => {
  try {
    const response = await requestWithRetry({
      url,
    });
    if (!response.data) return null;
    if (isValidPopoverResponse(response.data)) return response.data;
    return response.data;
  } catch (error: any) {
    logger.info(
      `asyncRequests - textLinkPopoverRemote get request for endpoint ${url}. error: ${error}`,
    );
    return null;
  }
};

export const AttributesTextLinkPopover: React.VFC<
  TextLinkPopoverRemote | TextLinkPopover
> = (props) => {
  const { label, content, ariaLabel, tooltipPosition, withCloseButton } = props;
  const { popoverContent } = props as TextLinkPopover;
  const { popoverAPI, apiCallFailureMessage } = props as TextLinkPopoverRemote;

  const isMobile = useIsMobile();
  const [isOpen, setIsOpen] = useState(false);
  const [tooltipContent, setTooltipContent] = useState<any>(null);

  useEffect(() => {
    if (popoverAPI) {
      const fetchPopoverContent = async () => {
        const response = await ajaxGetPopoverResponse(popoverAPI);
        setTooltipContent(response);
      };
      fetchPopoverContent();
    }
  }, []);

  if (!popoverContent && (!popoverAPI || !apiCallFailureMessage)) return null;

  const handleToggle = () => setIsOpen(!isOpen);
  const handleClose = () => setIsOpen(false);

  const TooltipContent = () =>
    popoverContent ? (
      <MultipleLines lines={popoverContent} />
    ) : (
      <DynamicPopover popover={tooltipContent ?? apiCallFailureMessage} />
    );

  const trigger = isMobile ? (
    <TextLinkValue fontSize="base">
      {content}
      <TextLinkIcon type={angleDown} size="xs" />
    </TextLinkValue>
  ) : (
    <TextLinkButton onClick={handleToggle} aria-label={ariaLabel}>
      {content}
      <TextLinkIcon type={angleDown} size="xs" />
    </TextLinkButton>
  );

  return (
    <>
      <KeyLabel label={label} />
      {isMobile ? (
        <AdaptiveHelpTip
          renderTrigger={trigger}
          tooltipProps={{ withCloseButton: false }}
        >
          <TooltipContent />
        </AdaptiveHelpTip>
      ) : (
        <Popper
          isOpen={isOpen}
          trigger={trigger}
          onCloseButtonClick={handleClose}
          position={tooltipPosition}
          withCloseButton={withCloseButton}
        >
          <TooltipContent />
        </Popper>
      )}
    </>
  );
};
